import { ErrorHandler, Injectable } from '@angular/core';
import { WebAuth } from 'auth0-js';
import { environment } from '../../environments/environment';
import { LocalStorageService } from '../services/local-storage.service';
import { toString, trim } from 'lodash-es';
import { ClientStoreService } from '../services/client-store.service';
import { blank, data_get, data_is_false, fallback, filled, map_to_object, OptionalValue, spf, str_trim } from '../shared/utils/common';
import { AuthenticatedUser } from '../module/user/types';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, retryWhen, scan, take, tap } from 'rxjs/operators';
import { LayoutDashboardService } from '../shared/layouts/layout-dashboard/layout-dashboard.service';
import { NotificationService } from '../services/notification.service';
import { ApiService } from '../services/api.service';
import { HttpErrorResponse } from '@angular/common/http';

const _MAX_RENEWAL_RETRY_TIMES = 5;

@Injectable()
export class AuthService {
  private readonly _auth0 = new WebAuth({
    clientID: 'LO-CxQTAqr4042mcRhKmArDqn7uhX5-m',
    domain: 'login.fieldmagic.co',
    responseType: 'token id_token',
    redirectUri: environment.app_url + '/#account/callback',
    scope: 'openid profile email'
  });

  private readonly _localStoreKeys = [
    'access_token',
    'old_access_token',
    'current_client',
    'client_list',
    'auth0_access_token',
    'auth_id',
    'expires_at',
  ];

  constructor(
    private readonly _store: LocalStorageService,
    private readonly _clients: ClientStoreService,
    private readonly _layout: LayoutDashboardService,
    private readonly _reporter: ErrorHandler,
    private readonly _notifications: NotificationService,
    private readonly _api: ApiService,
  ) { }

  /**
   * Triggers an authentication process
   */
  public login(opts: {
    store_redirect?: boolean,
  } = {}): void {
    opts = Object.assign({
      store_redicect: false,
    }, opts);

    /// store redirect path for later use when specified
    if (opts.store_redirect) {
      this._storeRedirectPath();
    }

    // Call the authentication handler from auth0
    this._auth0.authorize();
  }

  /**
   * Handles the callback from the auth0 authentication
   */
  authorize(): void {
    const fromAuth0Redirect = window.location.hash.includes('access_token=')
      && window.location.hash.startsWith('#');

    /// most likely came from the auth process
    if (fromAuth0Redirect) {
      (new Observable((observer) => this._auth0.parseHash((err, result) => {
        if (filled(err)) {
          observer.error(err);
        } else {
          observer.next(result);
        }

        observer.complete();
      })))
        .pipe(
          tap({
            next: () => (result) => blank(result) && this.login(),
            error: () => this.login(),
          }),
          filter(filled),
          tap(() => window.location.hash = ''),
          tap((result) => this._setSession(result)),
        )
        .subscribe(() => this._storeRedirectOrSwitch());

      return;
    }

    /// renew token instead
    this.renewToken({
      authorized_on_failure: true,
    }).subscribe(() => this._storeRedirectOrSwitch());
  }

  /**
   * Clears all tokens from local storage
   */
  logout(): Observable<void> {
    // Remove everything in local storage on logout.
    this.clearStorage({
      all: true,
    });

    const promise: Promise<void> = this._auth0.logout({
      returnTo: spf('%s/#/account/logout', {
        args: [
          str_trim(environment.app_url, {
            chars: '/',
          }),
        ],
      }),
    });

    return from(promise);
  }

  /**
   * Clears all tokens from local storage
   */
  public clearStorage(opts: {
    all?: boolean,
  } = {}): void {
    opts = Object.assign({
      all: true,
    }, opts);

    this._store.clear({
      only: (!opts.all) ? this._localStoreKeys : [],
    });
  }

  /**
   * Indicates whether we currently have a valid authentication token
   */
  isAuthenticated(): Observable<boolean> {
    const source$ = new Observable<boolean>((observer) => this._auth0.checkSession({}, (err, result) => {
      return filled(err) ? observer.error(err) : observer.next(result);
    }));

    return source$.pipe(
      map(() => true),
      tap({
        error: (err) => console.error(`User is not authenticated due to error: ${err}`, err),
      }),
      catchError(() => of(false)),
      take(1),
    );
  }

  /**
   * Calls Auth0 to renew the token
   */
  renewToken(opts: {
    redirect?: boolean;
    authorized_on_failure?: boolean;
    once?: boolean;
  } = {}): Observable<boolean> {
    opts = Object.assign({
      authorized_on_failure: false,
      once: false,
    }, opts);

    /// refactor token renewal
    const source$ = new Observable<Record<string, any>>((observer) => this._auth0.checkSession({}, (err, result) => {
      if (err) {
        observer.error(err);
      } else {
        observer.next(result);
      }

      observer.complete();
    }));

    return source$.pipe(
      map((result) => {
        if (blank(result)) {
          throw {
            'code': 'no_token_generated'
          };
        }

        this._setSession(result);

        const stored = this._store.getItem('image');

        if (filled(stored)
          && filled(data_get(result, 'idTokenPayload.picture'))
        ) {
          this._store.setItem('temp_image', data_get(result, 'idTokenPayload.picture'));
        }

        return true;
      }),
      /// retryWhen will be deprecated on v9
      /// see: https://rxjs.dev/api/operators/retryWhen#deprecation-notes
      retryWhen((errors$) => errors$.pipe(
        retry(_MAX_RENEWAL_RETRY_TIMES),
        scan((attempts, error) => {
          if ((
            data_get(error, 'code') === 'login_required'
            || data_get(error, 'code') === 'no_token_generated'
            || data_get(error, 'code') === 'invalid_token'
            || data_get(error, 'code') === 'timeout'
          )
            && attempts < _MAX_RENEWAL_RETRY_TIMES
            && data_is_false(opts.once)
          ) {
            console.info('retrying....');
            return attempts + 1;
          }

          console.info('retry exhauseted. throwing the error.');

          throw error;
        }, 0),
      )),
      catchError((err) => {
        console.error(`Failed to renew token due to error: ${err}`, err);

        if (opts.authorized_on_failure) {
          this.login();
          return EMPTY;
        }

        /// do we have active client? if so store the current redirect path and ask
        /// to login
        if (!opts.authorized_on_failure && filled(this._clients.getActiveClient())) {
          this._storeRedirectPath();
        }

        this._reporter.handleError(err);

        return this._notifications.sendConfirmation('We are unable to refresh your token due to error. Do you wish to login?')
          .pipe(
            tap((confirm) => {
              if (data_is_false(data_get(confirm, 'answer'))) {
                return;
              }

              return this.login();
            }),
            map(() => false),
          );
      }),
    );
  }

  getCurrentUser(): OptionalValue<AuthenticatedUser> {
    const id = this._store.getItem('user_id');

    if (blank(id)) {
      return null;
    }

    const name = this._store.getItem('user_name')
    const email = this._store.getItem('user_email');

    return {
      id,
      email_address: email,
      name,
      display_text: fallback(name, {
        to: () => email,
      })
    };
  }

  clearRedirectPath(): void {
    this._store.removeItem('redirect_path');
  }

  who = (): Observable<AuthenticatedUser> => this._api.callPrivate$({
    path: '/account/whoami',
    onSuccessResponse: (data) => ({
      ... map_to_object(data),
      id: data.get('user_id'),
      name: data.get('user_name'),
      display_text: fallback(data.get('user_name'), {
        to: () => data.get('email_address'),
      }),
      email_address: data.get('email_address'),
    }),
    ignoreForbiddenError: true,
  }).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse
        && err.status === 403
        && data_get(err.error, 'message') == 'email_not_verified'
      ) {
        this._notifications.notifyError('Account is not yet verified. Please make sure to verify your account.');
        return EMPTY;
      }

      return throwError(err);
    }),
  );

  private _storeRedirectPath(): void {
    const path = window.location.hash;

    /// if path is blank or contains access_token we don't want to store it
    if (blank(path) || toString(path).includes('access_token')) {
      return;
    }

    /// make sure that path don't contain logout/login or account/selection
    const regex = /\b(login|(?:do)?logout|account\/selection)\b/i;

    if (regex.test(path)) {
      return;
    }

    this._store.setItem('redirect_path', trim(path, '#'));
  }

  private _setSession(authResult): void {
    this._store.setItem('auth0_access_token', authResult.accessToken);
    this._store.setItem('access_token', authResult.idToken);
    this._store.setItem('auth_id', data_get(authResult, 'idTokenPayload.sub'));
  }

  private _storeRedirectOrSwitch(): void {
    if (filled(this._clients.getActiveClient())) {
      return this._storeRedirectPath();
    }

    return this._layout.switchClient();
  }
}

export const ACCT_EMAIL_NOT_VERIFIED_ERROR = 'email_not_verified';